import React, { useState, useMemo } from "react";
import ExhibitorCard from "../../../../widgets/ExhibitorCard";
import {
  Box,
  Grid,
  Pagination,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Typography,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import CustomCircularP from "../../../../widgets/CustomCircularP";
import AlphabetSortWidget from "./AlphabetSortWidget";
import ExhibitorFilterBar from "./ExhibitorFilterBar";
import useApiEp from "../../../../hooks/useApiEp";

const Exhibitors = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("sm"));

  // States
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' = A to Z, 'desc' = Z to A
  const [filters, setFilters] = useState({
    search: "",
    hall: "",
    country: "",
    cat: [],
  });

  // Fetch Data
  const params = new URLSearchParams({
    page: String(page),
    sq: filters.search,
    hall: filters.hall,
    country: filters.country,
    letter: selectedLetter,
  });
  if (filters.cat.length > 0) {
    // JSON keeps category names containing commas intact during multiselect.
    params.set("cat", JSON.stringify(filters.cat));
  }
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-exhibitors?${params.toString()}`
  );

  // Client-side sorting A-Z / Z-A
  const sortedExhibitors = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];
    const list = [...data.data];

    return list.sort((a, b) => {
      const nameA = (
        a.name ||
        a.company_name ||
        a.title ||
        a.exhibitor_name ||
        ""
      )
        .toString()
        .trim()
        .toLowerCase();
      const nameB = (
        b.name ||
        b.company_name ||
        b.title ||
        b.exhibitor_name ||
        ""
      )
        .toString()
        .trim()
        .toLowerCase();

      if (!nameA) return 1;
      if (!nameB) return -1;

      return sortOrder === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [data, sortOrder]);

  // --- Handlers ---
  const onPageChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleSearch = (searchTerm) => {
    setFilters((prev) => ({ ...prev, search: searchTerm }));
    setPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      hall: newFilters.hall !== undefined ? newFilters.hall : prev.hall,
      country:
        newFilters.country !== undefined ? newFilters.country : prev.country,
      cat: newFilters.cat !== undefined ? newFilters.cat : prev.cat,
    }));
    setPage(1);
  };

  const handleLetterSelect = (newV) => {
    setSelectedLetter(newV || "");
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // --- Helpers ---
  const totalPages = data?.totalItems ? Math.ceil(data.totalItems / 30) : 0;
  const hasExhibitors = sortedExhibitors && sortedExhibitors.length > 0;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <CustomCircularP show={isLoading} />

      <div>
        <ExhibitorFilterBar onSearch={handleSearch} onFilter={handleFilter} />

        <Box sx={{ mb: 3, width: "100%" }}>
          <AlphabetSortWidget
            selectedLetter={selectedLetter}
            onSelectLetter={handleLetterSelect}
            sortOrder={sortOrder}
            onSortToggle={handleSortToggle}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </Box>

        {/* List Content */}
        {!isLoading && !hasExhibitors ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="30vh"
          >
            <Typography variant="h6" color="textSecondary">
              No exhibitors found matching your criteria.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sortedExhibitors.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={viewMode === "grid" ? 6 : 12}
                md={viewMode === "grid" ? 4 : 12}
                lg={viewMode === "grid" ? 3 : 12}
                key={item.id || index}
              >
                <ExhibitorCard item={item} viewMode={viewMode} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box mt={4} mb={2} justifyContent="center" display="flex">
            <Pagination
              count={totalPages}
              page={page}
              onChange={onPageChange}
              color="primary"
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

export default Exhibitors;
